import { ThunkAction } from '../types';
import type { CallApiResult } from '@tmapy/beetlejuice-utils';

import { callApi } from '../../utils/api';
import { DOC_STATE } from '../../docs/constants';

export const INVALIDATE_CACHE = 'INVALIDATE_CACHE';
export const RESOURCE_LOAD_START = 'RESOURCE_LOAD_START';
export const RESOURCE_LOAD_SUCCESS = 'RESOURCE_LOAD_SUCCESS';
export const RESOURCE_LOAD_FAILURE = 'RESOURCE_LOAD_FAILURE';
export const RESOURCE_INVALIDATE = 'RESOURCE_INVALIDATE';
export const RESOURCE_PATCH_SSE = 'RESOURCE_PATCH_SSE';

export const actionInvalidateCache = () => ({
  type: INVALIDATE_CACHE as typeof INVALIDATE_CACHE,
});

export const actionResourceLoadStart = (url: string, appendTo?: string) => ({
  type: RESOURCE_LOAD_START as typeof RESOURCE_LOAD_START,
  meta: { url, appendTo },
});

export const actionResourceLoadSuccess = (
  url: string,
  payload: CallApiResult<object | null>,
  appendTo?: string,
) => ({
  type: RESOURCE_LOAD_SUCCESS as typeof RESOURCE_LOAD_SUCCESS,
  payload,
  meta: { url, appendTo },
});

export const actionResourceLoadFailure = (
  url: string,
  payload: CallApiResult<object | null> | Error,
) => ({
  type: RESOURCE_LOAD_FAILURE as typeof RESOURCE_LOAD_FAILURE,
  error: true as const,
  payload,
  meta: { url },
});

export const actionResourceInvalidate = (path: string) => ({
  type: RESOURCE_INVALIDATE as typeof RESOURCE_INVALIDATE,
  payload: { url: `${globalThis.DMX_CONFIG.server}${path}` },
});

export type ResourceAction =
  | ReturnType<typeof actionInvalidateCache>
  | ReturnType<typeof actionResourceLoadStart>
  | ReturnType<typeof actionResourceLoadSuccess>
  | ReturnType<typeof actionResourceLoadFailure>
  | ReturnType<typeof actionResourceInvalidate>
  | ReturnType<typeof actionResourcePatchSSE>;

const flyingRequests = new Map<string, Promise<CallApiResult<any>>>();

export const actionResourceLoad =
  <ResourceType extends object>(url: string, appendTo?: string): ThunkAction =>
  async (dispatch): Promise<CallApiResult<ResourceType>> => {
    const flyingRequest = flyingRequests.get(url);
    if (flyingRequest) return flyingRequest;

    try {
      const responsePromise = callApi<ResourceType>(url, 'GET');
      flyingRequests.set(url, responsePromise);
      dispatch(actionResourceLoadStart(url, appendTo));
      try {
        const response = await responsePromise;
        if (response.error) {
          dispatch(actionResourceLoadFailure(url, response));
        } else {
          dispatch(actionResourceLoadSuccess(url, response, appendTo));
        }
        return response;
      } finally {
        flyingRequests.delete(url);
      }
    } catch (error) {
      dispatch(actionResourceLoadFailure(url, error as Error));
      return { ok: false, status: 0, error: error as any, data: null, fetchedAt: Date.now() };
    }
  };

export const actionResourcePatchSSE = (payload: {
  url: string;
  id: string | number;
  type: string;
  data: { id?: number | string; state: DOC_STATE };
}) =>
  ({
    type: RESOURCE_PATCH_SSE,
    payload,
  }) as const;
